import { useState, useEffect, useRef } from 'react';
import { Check, X, AlertTriangle, Info } from 'lucide-react';

const InspectionChecklist = ({ inspectionType, onChecklistChange, initialData = {} }) => {
  const [checklistItems, setChecklistItems] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const preTrip = [
    {
      category: 'Engine Compartment',
      items: [
        { id: 'engine_oil', label: 'Engine Oil Level', critical: true },
        { id: 'coolant_level', label: 'Coolant Level', critical: true },
        { id: 'brake_fluid', label: 'Brake Fluid Level', critical: true },
        { id: 'power_steering', label: 'Power Steering Fluid', critical: false },
        { id: 'belt_condition', label: 'Belt Condition', critical: false },
        { id: 'battery_terminals', label: 'Battery Terminals Clean', critical: false }
      ]
    },
    {
      category: 'Exterior Vehicle',
      items: [
        { id: 'tire_condition', label: 'Tire Condition & Pressure', critical: true },
        { id: 'wheel_lugs', label: 'Wheel Lug Nuts Secure', critical: true },
        { id: 'mirrors_clean', label: 'Mirrors Clean & Secure', critical: true },
        { id: 'lights_functioning', label: 'All Lights Functioning', critical: true },
        { id: 'horn_working', label: 'Horn Working', critical: true },
        { id: 'wipers_condition', label: 'Wipers & Washers', critical: false }
      ]
    },
    {
      category: 'Interior & Safety',
      items: [
        { id: 'steering_wheel', label: 'Steering Wheel Free Play', critical: true },
        { id: 'seat_belts', label: 'Seat Belts Working', critical: true },
        { id: 'parking_brake', label: 'Parking Brake', critical: true },
        { id: 'service_brakes', label: 'Service Brakes', critical: true },
        { id: 'emergency_equipment', label: 'Emergency Equipment Present', critical: true },
        { id: 'fire_extinguisher', label: 'Fire Extinguisher', critical: true }
      ]
    }
  ];

  const postTrip = [
    {
      category: 'Vehicle Condition',
      items: [
        { id: 'damage_exterior', label: 'Exterior Damage Check', critical: true },
        { id: 'damage_interior', label: 'Interior Damage Check', critical: true },
        { id: 'fluid_leaks', label: 'Fluid Leaks Under Vehicle', critical: true },
        { id: 'unusual_noises', label: 'Unusual Noises During Trip', critical: false }
      ]
    },
    {
      category: 'Performance Issues',
      items: [
        { id: 'engine_performance', label: 'Engine Performance Issues', critical: true },
        { id: 'brake_performance', label: 'Brake Performance Issues', critical: true },
        { id: 'steering_issues', label: 'Steering Issues', critical: true },
        { id: 'transmission_issues', label: 'Transmission Issues', critical: true }
      ]
    },
    {
      category: 'Documentation & Cleanliness',
      items: [
        { id: 'fuel_level', label: 'Fuel Level Recorded', critical: false },
        { id: 'mileage_recorded', label: 'Mileage Recorded', critical: false },
        { id: 'interior_clean', label: 'Interior Cleaned', critical: false },
        { id: 'logbook_updated', label: 'Logbook Updated', critical: false }
      ]
    }
  ];

  const categories = inspectionType === 'pre-trip' ? preTrip : postTrip;

  useEffect(() => {
    setChecklistItems(initialData);
    setIsInitialized(true);
  }, [initialData]);

  // Use a ref to store the callback to prevent dependency issues
  const onChecklistChangeRef = useRef(onChecklistChange);
  
  // Update the ref when the callback changes
  useEffect(() => {
    onChecklistChangeRef.current = onChecklistChange;
  }, [onChecklistChange]);
  
  // Call the callback only when checklistItems changes, not when the callback changes
  // Skip the initial call to prevent infinite loop
  useEffect(() => {
    if (isInitialized) {
      onChecklistChangeRef.current(checklistItems);
    }
  }, [checklistItems, isInitialized]);

  const handleItemCheck = (itemId, status, notes = '') => {
    const updatedItems = {
      ...checklistItems,
      [itemId]: {
        status,
        notes,
        timestamp: new Date().toISOString()
      }
    };
    setChecklistItems(updatedItems);
  };

  const getItemStatus = (itemId) => {
    return checklistItems[itemId] || { status: 'unchecked', notes: '', timestamp: null };
  };

  const getCategoryProgress = (category) => {
    const totalItems = category.items.length;
    const checkedItems = category.items.filter(item => 
      getItemStatus(item.id).status !== 'unchecked'
    ).length;
    return { checked: checkedItems, total: totalItems };
  };

  const getOverallProgress = () => {
    const allItems = categories.flatMap(cat => cat.items);
    const totalItems = allItems.length;
    const checkedItems = allItems.filter(item => 
      getItemStatus(item.id).status !== 'unchecked'
    ).length;
    const failedCritical = allItems.filter(item => 
      item.critical && getItemStatus(item.id).status === 'fail'
    ).length;
    
    return { 
      checked: checkedItems, 
      total: totalItems, 
      progress: Math.round((checkedItems / totalItems) * 100),
      failedCritical
    };
  };

  const ChecklistItem = ({ item, onItemCheck, getItemStatus }) => {
    const itemStatus = getItemStatus(item.id);
    const [showNotes, setShowNotes] = useState(itemStatus.status === 'fail' || itemStatus.status === 'na');
    const [notes, setNotes] = useState(itemStatus.notes || '');

    // Update local notes state when itemStatus changes
    useEffect(() => {
      setNotes(itemStatus.notes || '');
      setShowNotes(itemStatus.status === 'fail' || itemStatus.status === 'na');
    }, [itemStatus.notes, itemStatus.status]);

    const handleStatusChange = (status) => {
      if (status === 'fail' || status === 'na') {
        // For fail/na, show notes input but don't submit yet
        setShowNotes(true);
        // Update the status immediately so buttons show selected state
        onItemCheck(item.id, status, notes);
      } else {
        // For pass, submit immediately without notes
        setShowNotes(false);
        setNotes('');
        onItemCheck(item.id, status, '');
      }
    };

    const handleNotesSubmit = () => {
      onItemCheck(item.id, itemStatus.status, notes);
      setShowNotes(false);
    };

    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">{item.label}</span>
              {item.critical && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Critical
                </span>
              )}
            </div>
            {itemStatus.status === 'fail' && itemStatus.notes && (
              <p className="text-sm text-red-600 mt-1 italic">{itemStatus.notes}</p>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStatusChange('pass');
            }}
            type="button"
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              itemStatus.status === 'pass'
                ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-green-50'
            }`}
          >
            <Check className="h-4 w-4 mr-1" />
            Pass
          </button>
          
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStatusChange('fail');
            }}
            type="button"
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              itemStatus.status === 'fail'
                ? 'bg-red-100 text-red-800 border-2 border-red-300'
                : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-red-50'
            }`}
          >
            <X className="h-4 w-4 mr-1" />
            Fail
          </button>

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStatusChange('na');
            }}
            type="button"
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              itemStatus.status === 'na'
                ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-yellow-50'
            }`}
          >
            <Info className="h-4 w-4 mr-1" />
            N/A
          </button>
        </div>

        {showNotes && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes Required{item.critical ? ' (Critical Item)' : ''}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Explain the issue or why this item is not applicable..."
              className="w-full h-20 p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex justify-end mt-2 space-x-2">
              <button
                onClick={() => setShowNotes(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleNotesSubmit}
                disabled={!notes.trim()}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Notes
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const progress = getOverallProgress();

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {inspectionType === 'pre-trip' ? 'Pre-Trip' : 'Post-Trip'} Checklist
          </h2>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">{progress.progress}%</div>
            <div className="text-sm text-gray-600">
              {progress.checked} of {progress.total} items
            </div>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.progress}%` }}
          />
        </div>

        {progress.failedCritical > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Critical Items Failed
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {progress.failedCritical} critical item(s) failed inspection. 
                  Vehicle should not be operated until resolved.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Checklist Categories */}
      {categories.map((category) => {
        const categoryProgress = getCategoryProgress(category);
        
        return (
          <div key={category.category} className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{category.category}</h3>
                <span className="text-sm text-gray-600">
                  {categoryProgress.checked}/{categoryProgress.total} completed
                </span>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {category.items.map((item) => (
                <ChecklistItem 
                  key={item.id} 
                  item={item} 
                  onItemCheck={handleItemCheck}
                  getItemStatus={getItemStatus}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InspectionChecklist;